import React, { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { TerminalContract } from '../../services/openapi';
import {
    DraftTerminalContract,
    createExistingDraft,
    createNewDraft,
    TerminalContractDraftData
} from './TerminalContractDraft';

// use DraftTerminalContract everywhere
type TerminalContractsDraftContextType = {
    drafts: DraftTerminalContract[];
    setInitialDrafts: (contracts: TerminalContract[]) => void;
    upsertDraft: (draft: DraftTerminalContract) => void;
    addNewDraft: (terminalId: number, poi?: string, terminalDescription?: string) => DraftTerminalContract;
    removeDraft: (key: string) => void;
    getDraftByContractId: (id: number) => DraftTerminalContract | undefined;
    getDraftByKey: (key: string) => DraftTerminalContract | undefined;
    getFirstDraft: () => DraftTerminalContract | undefined;
    getUsedBrands: () => string[];
    getDraftForHost: (host: string) => DraftTerminalContract | undefined;
    clearDrafts: () => void;
};

const TerminalContractsDraftContext = createContext<TerminalContractsDraftContextType | undefined>(undefined);

// Exported so callers outside the provider (e.g. the post-login handler in
// App.tsx) can clear stale drafts without needing the provider mounted.
export const TERMINAL_CONTRACT_DRAFTS_STORAGE_KEY = 'terminal-contract-drafts-v1';
const STORAGE_KEY = TERMINAL_CONTRACT_DRAFTS_STORAGE_KEY;

const loadDrafts = (): DraftTerminalContract[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const saveDrafts = (drafts: DraftTerminalContract[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
};

export function TerminalContractsDraftProvider({ children }: { children: ReactNode }) {
    const [drafts, setDrafts] = useState<DraftTerminalContract[]>(() => loadDrafts());

    useEffect(() => {
        saveDrafts(drafts);
    }, [drafts]);

    // The provider only stays mounted while the user is on one of the
    // terminal-contract routes (see TerminalContractsDraftLayout). Clearing
    // drafts here on unmount covers every way of leaving the section
    // (browser back, sidebar/menu navigation, etc.), not just an explicit
    // Cancel/back button click.
    useEffect(() => {
        return () => {
            setDrafts([]);
            localStorage.removeItem(STORAGE_KEY);
        };
    }, []);

    const setInitialDrafts = (contracts: TerminalContract[]) => {
        setDrafts(prev => {
            if (prev.length > 0) {
                return prev;
            }
            return contracts.map(createExistingDraft);
        });
    };

    const upsertDraft = (draft: DraftTerminalContract) => {
        setDrafts(prev => {
            const existingIndex = prev.findIndex(d => d.key === draft.key);

            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = draft;
                return updated;
            }

            return [...prev, draft];
        });
    };

    const addNewDraft = (terminalId: number, poi?: string, terminalDescription?: string) => {
        const draft = createNewDraft(terminalId, poi, terminalDescription);
        setDrafts(prev => [...prev, draft]);
        return draft;
    };

    const removeDraft = (key: string) => {
        setDrafts(prev => prev.filter(d => d.key !== key));
    };

    const getDraftByContractId = (id: number) => {
        return drafts.find(d => d.current.id === id);
    };

    const getDraftByKey = (key: string) => {
        return drafts.find(d => d.key === key);
    };

    const getFirstDraft = () => {
        return drafts.length > 0 ? drafts[0] : undefined;
    }

    const getDraftForHost = (host: string) => {
        return drafts.find(d => d.current.host === host);
    }

    const getUsedBrands = (): string[] => {
        const brands = drafts.map(d => d.current.brand).filter(Boolean) as string[];
        return Array.from(new Set(brands));
    }

    const clearDrafts = () => {
        setDrafts([]);
        localStorage.removeItem(STORAGE_KEY);
    };

    const value = useMemo(() => ({
        drafts,
        setInitialDrafts,
        upsertDraft,
        addNewDraft,
        removeDraft,
        getDraftByContractId,
        getDraftByKey,
        getFirstDraft,
        getDraftForHost,
        getUsedBrands,
        clearDrafts
    }), [drafts]);

    return (
        <TerminalContractsDraftContext.Provider value={value}>
            {children}
        </TerminalContractsDraftContext.Provider>
    );
}

export function useTerminalContractsDraft() {
    const ctx = useContext(TerminalContractsDraftContext);
    if (!ctx) {
        throw new Error('useTerminalContractsDraft must be used inside TerminalContractsDraftProvider');
    }
    return ctx;
}